import { useState, useEffect } from 'react';
import { Calendar, User, MapPin, ChevronLeft, ChevronRight, Plus, Video, Stethoscope } from 'lucide-react';
import { api, Appointment } from '../lib/api';

const statusLabels: Record<string, { label: string; badge: string }> = {
    booked: { label: 'Agendada', badge: 'badge-neutral' },
    pending: { label: 'Pendiente', badge: 'badge-neutral' },
    confirmed: { label: 'Confirmada', badge: 'badge-primary' },
    arrived: { label: 'Llegó', badge: 'badge-warning' },
    in_progress: { label: 'En consulta', badge: 'badge-primary' },
    completed: { label: 'Completada', badge: 'badge-success' },
    cancelled: { label: 'Cancelada', badge: 'badge-danger' },
    no_show: { label: 'No asistió', badge: 'badge-danger' },
};

export function AppointmentsPage() {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDate, setCurrentDate] = useState(new Date());

    const dateStr = currentDate.toISOString().split('T')[0];

    useEffect(() => {
        loadAppointments();
    }, [dateStr]);

    async function loadAppointments() {
        setLoading(true);
        try {
            const res = await api.appointments.list(`date=${dateStr}&limit=50`);
            setAppointments(res.data);
        } catch {
            console.warn('Appointments API unavailable for', dateStr);
            setAppointments([]);
        } finally {
            setLoading(false);
        }
    }

    function navigateDay(delta: number) {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + delta);
        setCurrentDate(d);
    }

    return (
        <div className="page-content fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                <h1 className="page-title">Citas</h1>
                <button className="btn btn-primary" id="new-appointment-btn">
                    <Plus size={16} /> Nueva cita
                </button>
            </div>

            {/* Date Navigator */}
            <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-4)' }}>
                    <button className="btn btn-icon btn-secondary" onClick={() => navigateDay(-1)}>
                        <ChevronLeft size={20} />
                    </button>
                    <div style={{ textAlign: 'center', minWidth: 200 }}>
                        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>
                            {currentDate.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                            {appointments.length} citas programadas
                        </div>
                    </div>
                    <button className="btn btn-icon btn-secondary" onClick={() => navigateDay(1)}>
                        <ChevronRight size={20} />
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setCurrentDate(new Date())} style={{ marginLeft: 'var(--space-4)' }}>
                        Hoy
                    </button>
                </div>
            </div>

            {/* Timeline View */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="card" style={{ padding: 'var(--space-4)' }}>
                            <div className="skeleton" style={{ height: 60, width: '100%' }} />
                        </div>
                    ))
                ) : appointments.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-text-muted)' }}>
                        <Calendar size={48} style={{ marginBottom: 'var(--space-4)', opacity: 0.3 }} />
                        <p>No hay citas para este día</p>
                    </div>
                ) : (
                    appointments.map((appt) => {
                        const start = new Date(appt.scheduledStart);
                        const statusInfo = statusLabels[appt.status] ?? { label: appt.status, badge: 'badge-neutral' };

                        return (
                            <div key={appt.id} className="card" style={{
                                padding: 'var(--space-4)',
                                borderLeft: `4px solid ${appt.status === 'completed' ? 'var(--color-success)' : appt.status === 'in_progress' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                cursor: 'pointer',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
                                        <div style={{
                                            textAlign: 'center', minWidth: 60,
                                            padding: 'var(--space-2)', background: 'var(--color-bg)',
                                            borderRadius: 'var(--radius-md)',
                                        }}>
                                            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>
                                                {start.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                                {appt.durationMinutes} min
                                            </div>
                                        </div>

                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                <User size={14} color="var(--color-text-muted)" />
                                                <span style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>
                                                    {appt.patient.firstName} {appt.patient.lastName}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', display: 'flex', gap: 'var(--space-4)', marginTop: 2 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    {appt.appointmentType === 'telehealth' ? <Video size={12} /> : <Stethoscope size={12} />}
                                                    {appt.appointmentType === 'telehealth' ? 'Teleconsulta' : 'Presencial'}
                                                </span>
                                                {appt.location && (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        <MapPin size={12} />
                                                        {appt.location.name}
                                                    </span>
                                                )}
                                            </div>
                                            {appt.chiefComplaint && (
                                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                                                    Motivo: {appt.chiefComplaint}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <span className={`badge ${statusInfo.badge}`}>{statusInfo.label}</span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
